import { injectable, inject } from "tsyringe";
import { TOKEN_IProjectBus, type IProjectBus } from "@/services/ProjectBus";
import log from "loglevel";

export type ThemeMode = 'dark' | 'light' | 'system'

export const TOKEN_IDomThemes = Symbol('IDomThemes');

/**
 * Interface defining methods for theme management service.
 * Handles theme state, mode changes, and subscriptions to theme updates.
 * 
 * Provides functionality to:
 * - Control theme modes (dark/light/system)
 * - Check current theme state
 * - Toggle between themes
 */
export interface IDomThemes {
    /**
     * Get the current theme setting
     * @returns The current theme mode
     */
    getCurrentTheme(): ThemeMode;

    /**
     * Check if dark mode is currently active
     * @returns True if dark mode is active, false otherwise
     */
    isDarkMode(): boolean;

    /**
     * Set the theme mode
     * @param theme The theme mode to set
     */
    setTheme(theme: ThemeMode): void;

    /**
     * Toggle between light and dark themes
     */
    toggleTheme(): void;
}

@injectable()
export class DomThemes implements IDomThemes {
    private mediaQuery: MediaQueryList

    constructor(
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus
    ) {
        log.info("DomThemes: Initializing theme service");
        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        // Initialize theme from localStorage or system preference
        const savedTheme = localStorage.getItem('theme') as ThemeMode | null
        if (savedTheme && savedTheme !== 'system') {
            log.info(`DomThemes: Initializing with saved theme preference: ${savedTheme}`);
            this.setTheme(savedTheme)
        } else {
            log.info("DomThemes: No saved theme preference found, using system theme");
            this.setTheme('system')
        }

        // Listen for system theme changes
        this.mediaQuery.addEventListener('change', (e) => {
            if (this.getCurrentTheme() === 'system') {
                log.info(`DomThemes: System theme preference changed to ${e.matches ? 'dark' : 'light'}`);
                this.applyTheme('system')
                this.notifyThemeChanged()
            }
        })

        log.info(`DomThemes: Theme service initialized with ${this.getCurrentTheme()} mode (isDark: ${this.isDarkMode()})`);
    }

    private notifyThemeChanged(): void {
        log.info(`DomThemes: Notifying theme changed: ${this.getCurrentTheme()} (isDark: ${this.isDarkMode()})`);
        this.projectBus.themeChanged(this.getCurrentTheme(), this.isDarkMode());
    }

    public getCurrentTheme(): ThemeMode {
        if (!('theme' in localStorage)) {
            return 'system'
        }
        return (localStorage.getItem('theme') as ThemeMode) || 'system'
    }

    public isDarkMode(): boolean {
        return document.documentElement.classList.contains('dark')
    }

    private applyTheme(theme: ThemeMode): void {
        log.info(`DomThemes: Applying theme: ${theme}`);

        if (theme === 'system') {
            localStorage.removeItem('theme')
            const systemIsDark = this.mediaQuery.matches;
            log.info(`DomThemes: Using system preference: ${systemIsDark ? 'dark' : 'light'}`);
            document.documentElement.classList.toggle('dark', systemIsDark)
        } else {
            localStorage.theme = theme
            document.documentElement.classList.toggle('dark', theme === 'dark')
        }
    }

    public setTheme(theme: ThemeMode): void {
        log.info(`DomThemes: Setting theme to: ${theme}`);
        this.applyTheme(theme)
        this.notifyThemeChanged()
    }

    public toggleTheme(): void {
        const currentTheme = this.getCurrentTheme()
        let newTheme: ThemeMode;

        if (currentTheme === 'system') {
            newTheme = this.mediaQuery.matches ? 'light' : 'dark';
            log.info(`DomThemes: Toggling from system theme (${this.mediaQuery.matches ? 'dark' : 'light'}) to explicit ${newTheme} mode`);
        } else {
            newTheme = currentTheme === 'dark' ? 'light' : 'dark';
            log.info(`DomThemes: Toggling from ${currentTheme} to ${newTheme} mode`);
        }

        this.setTheme(newTheme)
    }
}